import { RawDraftContentState } from 'draft-js';
import { isEqual, isNumber, isString } from 'lodash';
import { CellData, RichTableEditorContent, TableEditorContent } from '../../../../../../../common/table/TableTypes';
import { isTiptapContent } from '../../../../../../../common/tiptap/utils/editorTypeUtils';
import doesEditorJsonHaveText from '../../../../../../../common/tiptap/utils/jsonContentUtils/doesEditorJsonHaveText';
import getTiptapNodeTextContent from '../../../../../../../common/tiptap/utils/jsonContentUtils/getTiptapNodeTextContent';
import {
    getDraftCellLevelTextStyles,
    getTiptapCellLevelTextStyles,
} from '../../../../bridge/utils/dataUtils/cell/tableCellStyleUtils';
import { applyInferredCellType } from '../../../../bridge/utils/dataUtils/cell/tableCellTypeInferenceUtils';
import {
    cellCanBeRichFormatted,
    cellIsReadOnly,
} from '../../../../bridge/utils/dataUtils/cell/tableCellTypeModelUtils';
import { isFormula } from '../../../../bridge/utils/dataUtils/formula/tableFormulaPredicateUtils';
import rawGetText from '../../../../../../../common/utils/editor/rawUtils/rawGetText';

/**
 * Returns whether incoming editor content should be saved as a populated value.
 */
const isCellContentPopulated = (content: TableEditorContent | number | undefined): boolean => {
    if (isString(content)) return content !== '';
    if (isNumber(content)) return true;

    return doesEditorJsonHaveText(content);
};

/**
 * Returns whether editor content is legacy Draft cell content.
 */
const isDraftTableEditorContent = (content: TableEditorContent | undefined): content is RawDraftContentState => {
    if (!content) return false;
    if (isString(content)) return false;

    return !isTiptapContent(content);
};

/**
 * Gets the user-visible editor text used to infer a cell type.
 */
const getTableEditorContentText = (content: TableEditorContent | undefined): string | undefined => {
    if (!content) return;
    if (isString(content)) return content;

    return isTiptapContent(content) ? getTiptapNodeTextContent(content) : rawGetText(content);
};

/**
 * Gets cell-level styles from rich editor content.
 */
const getCellLevelTextStylesFromEditorContent = (editorContent: RichTableEditorContent): string[] => {
    if (isTiptapContent(editorContent)) return getTiptapCellLevelTextStyles(editorContent);

    return getDraftCellLevelTextStyles(editorContent);
};

/**
 * Applies the text styles that are shared by the whole rich editor content.
 */
const applyCellLevelTextStyles = (cellData: CellData, cellLevelTextStyles: string[]): CellData => {
    const stylesMatch = isEqual([...cellLevelTextStyles].sort(), [...(cellData.textStyle || [])].sort());

    if (!stylesMatch || !cellCanBeRichFormatted(cellData)) {
        cellData.textStyle = cellLevelTextStyles;
    }

    return cellData;
};

/**
 * Builds the next persisted cell model from a raw editor update.
 */
export const createNewCellData = (
    prevCellData: CellData,
    hotCellValue: string | null,
    locale: string,
    currencyPreference: string,
    tableEditorContent?: TableEditorContent,
): CellData => {
    const editorContentText = getTableEditorContentText(tableEditorContent);

    // Rich editor content is authoritative while an editor is active, except HOT completes formula parentheses.
    // HOT also provides the value for non-editor changes like paste, autofill, undo/redo, etc.
    const cellValue = isFormula(hotCellValue) ? hotCellValue : editorContentText || hotCellValue;
    let newCellData = { ...prevCellData, value: cellValue };

    delete newCellData.draftContent;
    delete newCellData.tiptapContent;
    delete newCellData.resultValue;

    // If cell has no content then return empty cell data with a consistent format
    const hasContent = hotCellValue !== null && isCellContentPopulated(tableEditorContent || hotCellValue);
    if (!hasContent) {
        newCellData.value = null;

        if (cellIsReadOnly(newCellData)) {
            delete newCellData.type;
        }

        return newCellData;
    }

    newCellData = applyInferredCellType(newCellData, prevCellData.value, locale, currencyPreference);

    if (!cellCanBeRichFormatted(newCellData)) return newCellData;
    if (cellIsReadOnly(newCellData)) return newCellData;

    if (tableEditorContent && isTiptapContent(tableEditorContent)) {
        newCellData.tiptapContent = tableEditorContent;
        newCellData.value = getTiptapNodeTextContent(tableEditorContent);
        const cellLevelTextStyles = getCellLevelTextStylesFromEditorContent(tableEditorContent);
        return applyCellLevelTextStyles(newCellData, cellLevelTextStyles);
    }

    if (isString(tableEditorContent)) {
        newCellData.value = tableEditorContent;
        return newCellData;
    }

    if (isDraftTableEditorContent(tableEditorContent)) {
        newCellData.draftContent = tableEditorContent;
        newCellData.value = rawGetText(tableEditorContent);
        const cellLevelTextStyles = getCellLevelTextStylesFromEditorContent(tableEditorContent);
        return applyCellLevelTextStyles(newCellData, cellLevelTextStyles);
    }

    // Should be unreachable
    return newCellData;
};
